import React, { useState, useEffect } from "react";
import { Input, Button, Table, Modal, Form, message, Popconfirm, Tooltip, type TableProps, Space, Card, Switch, Spin, Col, Row, Segmented, Badge, Tag, Select } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, LoadingOutlined, EnvironmentOutlined, GlobalOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import axios from "axios";

const { TextArea } = Input;

interface Employer {
  key: string | number;
  _id: string;
  name: string;
  imageName?: string;
  canonical?: string;

  contactNumber?: string;
  aboutCompany?: string;
  constactPersonName?: string;
  companyWebsite?: string;
  companyBanner: string;
  employerSlug: string;
  workPolicy?: string[] | string;
  foundedIn?: string;
  employeeCount?: string;
  indiaHeadquarters?: string;
  officeLocations?: string;
  primaryIndustry?: string;
  leaderShip?: Array<{
    leaderImage: string;
    leaderName: string;
    leaderDestination: string;
  }>;
  benefits?: Array<{ title: string; items: string[] | string }>;
  companyAddress: string;
  companySocialMedia?: Array<{ platform: string; url: string }>;
  interviewFaq?: Array<{ question: string; answer: string }>;
  galleryImage?: Array<string>;
  csrActivities?: Array<{ link?: string; point?: string }>;
  isFooterShow?: boolean;
  published?: boolean;
  isPublished?: boolean;
  userPostDataInfo?: Array<{
    name: string;
    role: string;
    emailId: string;
    createdAt: string;
    updatedAt: string;
  }>;
  seo?: Array<{
    title: string;
    description: string;
    canonical: string;
    keywords: string[];
  }>;
}

const CreateEmployerComponent = () => {
  const [form] = Form.useForm();

  // State for the FULL list of data (sorted A-Z)
  const [allEmployers, setAllEmployers] = useState<Employer[]>([]);
  // State for the data currently visible in the table
  const [visibleEmployers, setVisibleEmployers] = useState<Employer[]>([]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editEmployerIndex, setEditEmployerIndex] = useState<string | number | null>(null);
  const [fileName, setFileName] = useState('');
  const [bannerFileName, setBannerFileName] = useState('');
  const [leaderImageFiles, setLeaderImageFiles] = useState<{ [key: number]: string }>({});
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  // Loading & Pagination States
  const [loading, setLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10); // Start showing 10 items
  const LOAD_BATCH_SIZE = 10;

  // Publish/unpublish filter for admin
  const [publishFilter, setPublishFilter] = useState<'all' | 'published' | 'unpublished'>('all');

  const [cityList, setCityList] = useState<any[]>([]);

  const getUserRole = (): string => {
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    return userDetails?.role || '';
  };
  const userRole = getUserRole();
  const isAdmin = userRole === 'admin';

  const [activeTab, setActiveTab] = useState<'employer' | 'city'>('employer');

  // 1. Fetch ALL data at once to ensure we can sort it A-Z correctly
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch a large page size to get everything (adjust 10000 if you have more records)
      const response = await fetch(`https://service.ireedindia.com/v1/get-employer?pageNumber=0&size=10000`);
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const responseData = await response.json();
      const rawData = Array.isArray(responseData.data) ? responseData.data : [];

      // 2. Sort ALL data A-Z immediately
      rawData.sort((a: Employer, b: Employer) => {
        const nameA = (a?.name || "").toString().toLowerCase();
        const nameB = (b?.name || "").toString().toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setAllEmployers(rawData);
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCityList = async () => {
    try {
      const response = await axios.post("https://service.ireedindia.com/v1/find-city", {
        sortOrder: "A-Z"
      });
      const data = Array.isArray(response.data) ? response.data : (Array.isArray(response.data.data) ? response.data.data : []);
      setCityList(data);
    } catch (error) {
      console.error("Error fetching city list:", error);
    }
  };

  // Initial Fetch
  useEffect(() => {
    fetchData();
    fetchCityList();
  }, []);

  // 3. Update "Visible" data whenever full list, search, filter, or load count changes
  useEffect(() => {
    let filtered = allEmployers;

    // Apply Search
    if (searchText) {
      filtered = filtered.filter(employer =>
        employer.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Apply Admin Filter
    if (isAdmin && publishFilter !== 'all') {
      filtered = filtered.filter(employer => {
        const isPub = employer.published ?? employer.isPublished ?? false;
        return publishFilter === 'published' ? isPub : !isPub;
      });
    }

    // Slice the data for "Load More" functionality
    // This ensures we only show the top 'visibleCount' items (A -> Z)
    setVisibleEmployers(filtered.slice(0, visibleCount));

  }, [allEmployers, searchText, publishFilter, visibleCount, isAdmin]);

  // Handle Load More Click
  const handleLoadMore = () => {
    setVisibleCount(prev => prev + LOAD_BATCH_SIZE);
  };

  const onFinish = async (values: any) => {
    try {
      if (typeof values.workPolicy === "string") {
        values.workPolicy = values.workPolicy.split(",").map((s: string) => s.trim());
      }

      if (Array.isArray(values.benefits)) {
        values.benefits = values.benefits.map((b: any) => ({
          ...b,
          items: typeof b.items === "string" ? b.items.split(",").map((s: string) => s.trim()) : b.items,
        }));
      }

      if (Array.isArray(values.seo)) {
        values.seo = values.seo.map((s: any) => ({
          ...s,
          keywords: typeof s.keywords === "string"
            ? s.keywords.split(",").map((k: string) => k.trim())
            : s.keywords,
        }));
      }

      // 👇 SEO debug log - check browser console
      console.log("Sending SEO payload:", JSON.stringify(values.seo, null, 2));

      if (fileName) values.imageName = fileName;
      if (bannerFileName) values.companyBanner = bannerFileName;
      if (galleryImages.length > 0) values.galleryImage = galleryImages;

      if (values.leaderShip && Array.isArray(values.leaderShip)) {
        values.leaderShip = values.leaderShip.map((leader: any, index: number) => ({
          ...leader,
          leaderImage: leaderImageFiles[index] || leader.leaderImage
        }));
      }

      const userDetailsString = localStorage.getItem('userDetails');
      const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
      const userId = userDetails?.userId;

      if (editEmployerIndex !== null) {
        await axios.patch(`https://service.ireedindia.com/v1/edit-employer/${editEmployerIndex}`, values);
        message.success("Employer updated successfully");
      } else {
        const employerPayload = {
          ...values,
          userId: userId
        };
        await axios.post(`https://service.ireedindia.com/v1/create-employer`, employerPayload);
        message.success("Employer added successfully");
      }

      fetchData();
      form.resetFields();
      setIsModalVisible(false);
      setFileName("");
      setBannerFileName("");
      setLeaderImageFiles({});
      setGalleryImages([]);
      setEditEmployerIndex(null);

    } catch (error) {
      console.error("Error adding or updating employer:", error);
      message.error("Failed to save employer data");
    }
  };

  const handleEdit = (record: Employer) => {
    setIsModalVisible(true);         // Open modal FIRST
    setEditEmployerIndex(record._id);
    setFileName(record.imageName || '');
    setBannerFileName(record.companyBanner || '');
    setGalleryImages(record.galleryImage && Array.isArray(record.galleryImage) ? record.galleryImage : []);

    // Set form values AFTER modal is open (setTimeout ensures DOM is ready)
    setTimeout(() => {
      form.setFieldsValue({
        ...record,
        workPolicy: Array.isArray(record.workPolicy) ? record.workPolicy.join(', ') : record.workPolicy,
        benefits: Array.isArray(record.benefits)
          ? record.benefits.map(b => ({
            ...b,
            items: Array.isArray(b.items) ? b.items.join(', ') : b.items
          }))
          : record.benefits,
        seo: Array.isArray(record.seo)
          ? record.seo.map(s => ({
            title: s.title || '',
            description: s.description || '',
            canonical: s.canonical || '',
            keywords: Array.isArray(s.keywords) ? s.keywords.join(', ') : (s.keywords || ''),
          }))
          : [],
      });
    }, 0);
  };

  const handleDelete = async (record: Employer) => {
    if (!isAdmin) {
      message.error('You do not have permission to delete employers');
      return;
    }
    try {
      await axios.delete(`https://service.ireedindia.com/v1/delete-employer/${record._id}`);
      message.success("Employer deleted successfully");
      // Update local state to avoid full refetch
      setAllEmployers(prev => prev.filter(item => item._id !== record._id));
    } catch (error) {
      console.error("Error deleting record:", error);
      message.error("Failed to delete employer");
    }
  };

  const handleFooterShowToggle = async (employer: Employer, checked: boolean) => {
    if (!isAdmin) {
      message.error('You do not have permission to change footer visibility');
      return;
    }
    try {
      setAllEmployers(prev =>
        prev.map(e => e._id === employer._id ? { ...e, isFooterShow: checked } : e)
      );
      await axios.patch(`https://service.ireedindia.com/v1/edit-employer/${employer._id}`, { isFooterShow: checked });
      message.success(`Footer visibility updated successfully`);
    } catch (error) {
      console.error("Error updating footer visibility:", error);
      message.error("Failed to update footer visibility");
      fetchData(); // Revert on error
    }
  };

  const handlePublishToggle = async (employer: Employer, checked: boolean) => {
    if (!isAdmin) {
      message.error('You do not have permission to change publish status');
      return;
    }
    try {
      setAllEmployers(prev =>
        prev.map(e => e._id === employer._id ? { ...e, published: checked } : e)
      );
      await axios.patch(`https://service.ireedindia.com/v1/edit-employer/${employer._id}`, { published: checked });
      message.success(`Employer ${checked ? 'published' : 'unpublished'} successfully`);
    } catch (error) {
      console.error("Error updating publish status:", error);
      message.error("Failed to update publish status");
      fetchData(); // Revert on error
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const formDataFile = new FormData();
    formDataFile.append('file', file);
    try {
      const response = await axios.post(`https://service.ireedindia.com/upload`, formDataFile);
      setFileName(response.data.filename);
      message.success("Logo uploaded successfully");
    } catch (error) {
      console.error('File upload failed:', error);
      message.error("Logo upload failed");
    }
  };

  const handleBannerUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const formDataFile = new FormData();
    formDataFile.append('file', file);
    try {
      const response = await axios.post(`https://service.ireedindia.com/upload`, formDataFile);
      setBannerFileName(response.data.filename);
      message.success("Banner uploaded successfully");
    } catch (error) {
      console.error('Banner upload failed:', error);
      message.error("Banner upload failed");
    }
  };

  const handleLeaderImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const formDataFile = new FormData();
    formDataFile.append('file', file);
    try {
      const response = await axios.post(`https://service.ireedindia.com/upload`, formDataFile);
      setLeaderImageFiles(prev => ({ ...prev, [index]: response.data.filename }));
      message.success("Leader image uploaded");
    } catch (error) {
      console.error('Leader image upload failed:', error);
      message.error("Leader image upload failed");
    }
  };

  const handleGalleryImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event?.target?.files;
    if (!files || files.length === 0) return;
    const uploadPromises = Array.from(files).map(async (file) => {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      try {
        const response = await axios.post(`https://service.ireedindia.com/upload`, formDataFile);
        return response.data.filename;
      } catch { return null; }
    });
    const uploadedFilenames = (await Promise.all(uploadPromises)).filter(Boolean) as string[];
    setGalleryImages(prev => [...prev, ...uploadedFilenames]);
    message.success(`${uploadedFilenames.length} image(s) uploaded`);
  };

  const removeGalleryImage = (index: number) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
  };

  const columns: TableProps<Employer>['columns'] = [
    { title: "Employer Name", dataIndex: "name", key: "name", fixed: 'left', width: 150 },
    { title: "Email", dataIndex: "constactPersonName", key: "constactPersonName", width: 200 },
    { title: "Contact Number", dataIndex: "contactNumber", key: "contactNumber", width: 150 },
    { title: "Canonical URL", dataIndex: "canonical", key: "canonical", width: 200 },
    { title: "Company Website", dataIndex: "companyWebsite", key: "companyWebsite", width: 200 },
    {
      title: "About Company",
      dataIndex: "aboutCompany",
      key: "aboutCompany",
      width: 250,
      render: (text) => (
        text ? (
          <Tooltip title={text}>
            <span>{text.length > 100 ? `${text.substring(0, 100)}...` : text}</span>
          </Tooltip>
        ) : "No Description"
      ),
    },
    {
      title: "Company Banner",
      dataIndex: "companyBanner",
      key: "companyBanner",
      width: 120,
      render: (banner) => (
        banner ? <img src={`https://service.ireedindia.com/image/${banner}`} alt="Banner" style={{ width: "80px", borderRadius: 4 }} /> : "No Banner"
      ),
    },
    {
      title: "Company Logo",
      dataIndex: "imageName",
      key: "imageName",
      width: 120,
      render: (logo) => (
        logo ? <img src={`https://service.ireedindia.com/image/${logo}`} alt="Logo" style={{ width: "80px", borderRadius: 4 }} /> : "No Logo"
      ),
    },
    ...(isAdmin ? [
      {
        title: "Created By",
        dataIndex: "userPostDataInfo",
        key: "createdBy",
        width: 180,
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          return (
            <div style={{ fontSize: '12px' }}>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>{user.name}</div>
              <div style={{ color: '#6b7280' }}>{user.emailId}</div>
              <div style={{ color: '#1890ff', fontSize: '11px' }}>{user.role}</div>
            </div>
          );
        },
      },
      {
        title: "Created At",
        dataIndex: "userPostDataInfo",
        key: "createdAt",
        width: 140,
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.createdAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
      {
        title: "Updated At",
        dataIndex: "userPostDataInfo",
        key: "updatedAt",
        width: 140,
        render: (userPostDataInfo: any) => {
          if (!userPostDataInfo || userPostDataInfo.length === 0) return 'N/A';
          const user = userPostDataInfo[0];
          const date = new Date(user.updatedAt);
          return (
            <div style={{ fontSize: '12px', color: '#4b5563' }}>
              <div>{date.toLocaleDateString()}</div>
              <div style={{ color: '#9ca3af' }}>{date.toLocaleTimeString()}</div>
            </div>
          );
        },
      },
      {
        title: "Published",
        dataIndex: "published",
        key: "published",
        width: 120,
        render: (published: boolean) => (
          <span style={{
            padding: '4px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: published ? '#d1fae5' : '#fef3c7',
            color: published ? '#065f46' : '#92400e'
          }}>
            {published ? '✓ Published' : 'Draft'}
          </span>
        ),
      },
    ] : []),
    {
      title: "Action",
      key: "action",
      width: 200,
      fixed: 'right',
      render: (_: any, record: Employer) => (
        <Space>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} type="primary" ghost size="small" />
          </Tooltip>

          {isAdmin && (
            <Tooltip title="Delete">
              <Button
                icon={<DeleteOutlined />}
                danger
                type="primary"
                ghost
                size="small"
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this employer? This action cannot be undone.")) {
                    handleDelete(record);
                  }
                }}
              />
            </Tooltip>
          )}

          {isAdmin && (
            <Tooltip title="Toggle Footer Visibility">
              <Switch
                checked={record.isFooterShow}
                onChange={(checked) => handleFooterShowToggle(record, checked)}
                size="small"
              />
            </Tooltip>
          )}

          {isAdmin && (
            <Tooltip title={record.published ? 'Unpublish' : 'Publish'}>
              <Button
                type={record.published ? "default" : "primary"}
                size="small"
                onClick={() => handlePublishToggle(record, !record.published)}
                style={{
                  backgroundColor: record.published ? '#faad14' : '#52c41a',
                  color: '#fff',
                  borderColor: 'transparent',
                  minWidth: '32px'
                }}
              >
                {record.published ? 'U' : 'P'}
              </Button>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  // Calculate filtered totals for "Show End of List" logic
  let filteredTotal = allEmployers.length;
  if (searchText) {
    filteredTotal = allEmployers.filter(e => e.name.toLowerCase().includes(searchText.toLowerCase())).length;
  } else if (isAdmin && publishFilter !== 'all') {
    filteredTotal = allEmployers.filter(e => {
      const isPub = e.published ?? e.isPublished ?? false;
      return publishFilter === 'published' ? isPub : !isPub;
    }).length;
  }

  const hasMore = visibleEmployers.length < filteredTotal;

  const renderEmployerManagement = () => (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Employer Management</h2>
            <p style={{ margin: '4px 0 0 0', color: '#666' }}>Manage all employer profiles (A-Z)</p>
          </div>
          <Space>
            <Input.Search
              placeholder="Search by employer name..."
              onChange={(e) => {
                setSearchText(e.target.value);
                // Reset visible count when searching so user sees results immediately
                setVisibleCount(10);
              }}
              style={{ width: 300 }}
              allowClear
            />
            {isAdmin && (
              <div style={{ marginLeft: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 500, fontSize: 15 }}>Filter:</span>
                <select
                  value={publishFilter}
                  onChange={e => {
                    setPublishFilter(e.target.value as 'all' | 'published' | 'unpublished');
                    setVisibleCount(10);
                  }}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 15 }}
                >
                  <option value="all">All</option>
                  <option value="published">Published</option>
                  <option value="unpublished">Unpublished</option>
                </select>
              </div>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => {
                form.resetFields();
                setIsModalVisible(true);
                setFileName("");
                setBannerFileName("");
                setLeaderImageFiles({});
                setGalleryImages([]);
                setEditEmployerIndex(null);
              }}
            >
              Add Employer
            </Button>
          </Space>
        </div>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={visibleEmployers}
          rowKey="_id"
          scroll={{ x: 1500 }}
          pagination={false}
        />

        {/* Load More Button Section */}
        <div style={{ textAlign: 'center', marginTop: 16, padding: '10px 0' }}>
          {loading && allEmployers.length === 0 ? (
            // Initial loading spinner
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          ) : (
            hasMore ? (
              <Button
                onClick={handleLoadMore}
                size="large"
                style={{ minWidth: 200 }}
              >
                Load More
              </Button>
            ) : (
              visibleEmployers.length > 0 && (
                <div style={{ color: '#888', fontStyle: 'italic' }}>
                  All {filteredTotal} employers loaded
                </div>
              )
            )
          )}
        </div>
      </Card>

      <Modal
        title={<div style={{ textAlign: "center", fontWeight: "bold" }}>{editEmployerIndex !== null ? "Edit Employer" : "Add Employer"}</div>}
        open={isModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
          setEditEmployerIndex(null);
          setFileName("");
          setBannerFileName("");
          setLeaderImageFiles({});
          setGalleryImages([]);
        }}
        footer={null}
        width={800}
      >
        <h1 className="text-center text-lg font-semibold p-4">
          All fields are compulsory to be filled {editEmployerIndex !== null ? "Edit Employer" : "Add Employer"}
        </h1>

        <Form
          form={form}
          name="employers"
          onFinish={onFinish}
          layout="vertical"
        >
          {/* ================= SECTION 1: BASIC INFO ================= */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Employer Name" name="name" rules={[{ required: false, message: "Please enter employer name" }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Employer Slug" name="employerSlug" rules={[{ required: false, message: "Please enter employer slug" }]}>
                <Input placeholder="unique-employer-slug" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="City" name="cityId">
                <Select placeholder="Select City" showSearch optionFilterProp="children">
                  {cityList.map(c => (
                    <Select.Option key={c._id} value={c._id}>{c.city}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* ================= SECTION 2: CONTACT DETAILS ================= */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Email" name="constactPersonName">
                <Input placeholder="Contact Email" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Contact Number" name="contactNumber">
                <Input placeholder="Phone Number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Website" name="companyWebsite">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="About Company" name="aboutCompany">
            <TextArea rows={4} />
          </Form.Item>

          {/* ================= SECTION 3: SEO ================= */}
          <Card title="SEO Configuration" style={{ marginBottom: 20 }} size="small" headStyle={{ backgroundColor: '#f0f2f5' }}>
            <Form.List name="seo">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      type="inner"
                      title={`SEO Entry ${name + 1}`}
                      extra={<Button onClick={() => remove(name)} danger type="text" size="small">Remove</Button>}
                      style={{ marginBottom: 16 }}
                    >
                      <Form.Item {...restField} name={[name, 'title']} label="Title" rules={[{ required: false }]}>
                        <Input placeholder="Page Title" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'description']} label="Description" rules={[{ required: false }]}>
                        <TextArea placeholder="Meta Description" rows={2} />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'canonical']} label="Canonical URL" rules={[{ required: false }]}>
                        <Input placeholder="https://example.com/page" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'keywords']} label="Keywords" rules={[{ required: false }]}>
                        <Input placeholder="keyword1, keyword2, keyword3" />
                      </Form.Item>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={() => add()} block>
                    + Add SEO Entry
                  </Button>
                </>
              )}
            </Form.List>
          </Card>

          {/* ================= SECTION 4: MEDIA (Banner, Logo, Gallery) ================= */}
          <Row gutter={16}>
            {/* --- Banner --- */}
            <Col span={8}>
              <Form.Item label="Company Banner" name="companyBanner" rules={[{ required: false, message: "Please upload company banner" }]}>
                <div style={{ border: '1px dashed #d9d9d9', padding: 10, borderRadius: 4 }}>
                  <label htmlFor="banner" className="form-label" style={{ cursor: 'pointer', display: 'block', color: '#1890ff' }}>Click to Upload Banner</label>
                  <input
                    type="file"
                    id="banner"
                    name="companyBanner"
                    onChange={handleBannerUpload}
                    accept="image/png,image/jpg,image/jpeg,image/webp"
                    className="form-control-file uix"
                    style={{ display: 'block', marginTop: '8px', width: '100%' }}
                  />
                  {bannerFileName && (
                    <div style={{ marginTop: '8px' }}>
                      <img
                        src={`https://service.ireedindia.com/image/${bannerFileName}`}
                        alt="Banner Preview"
                        style={{ width: "100%", height: "80px", objectFit: "cover", borderRadius: 4 }}
                      />
                    </div>
                  )}
                </div>
              </Form.Item>
            </Col>

            {/* --- Logo --- */}
            <Col span={8}>
              <Form.Item label="Company Logo" name="imageName">
                <div style={{ border: '1px dashed #d9d9d9', padding: 10, borderRadius: 4 }}>
                  <label htmlFor="logo" className="form-label" style={{ cursor: 'pointer', display: 'block', color: '#1890ff' }}>Click to Upload Logo</label>
                  <input
                    type="file"
                    id="logo"
                    name="imageName"
                    onChange={handleFileUpload}
                    accept="image/png,image/jpg,image/jpeg,image/webp"
                    className="form-control-file uix"
                    style={{ display: 'block', marginTop: '8px', width: '100%' }}
                  />
                  {fileName && (
                    <div style={{ marginTop: '8px' }}>
                      <img
                        src={`https://service.ireedindia.com/image/${fileName}`}
                        alt="Logo Preview"
                        style={{ width: "80px", height: "80px", objectFit: "contain", borderRadius: 4 }}
                      />
                    </div>
                  )}
                </div>
              </Form.Item>
            </Col>

            {/* --- Gallery --- */}
            <Col span={8}>
              <Form.Item label="Gallery Images" name="galleryImage">
                <div style={{ border: '1px dashed #d9d9d9', padding: 10, borderRadius: 4 }}>
                  <label htmlFor="gallery" className="form-label" style={{ cursor: 'pointer', display: 'block', color: '#1890ff' }}>Click to Upload Gallery</label>
                  <input
                    type="file"
                    id="gallery"
                    name="galleryImage"
                    onChange={handleGalleryImageUpload}
                    accept="image/png,image/jpg,image/jpeg,image/webp"
                    className="form-control-file uix"
                    style={{ display: 'block', marginTop: '8px', width: '100%' }}
                    multiple
                  />
                  {galleryImages.length > 0 && (
                    <div style={{ marginTop: '12px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      {galleryImages.map((image, index) => (
                        <div key={index} style={{ position: 'relative' }}>
                          <img
                            src={`https://service.ireedindia.com/image/${image}`}
                            alt={`Gallery ${index + 1}`}
                            style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: 4 }}
                          />
                          <Button
                            size="small"
                            danger
                            onClick={() => removeGalleryImage(index)}
                            style={{ position: 'absolute', top: -5, right: -5, width: 15, height: 15, fontSize: 10, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>
            </Col>
          </Row>

          {/* ================= SECTION 5: STANDARD DETAILS ================= */}

          <Form.Item label="Work Policy" name="workPolicy">
            <Input placeholder="e.g. Remote, Hybrid, Onsite" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Founded In" name="foundedIn">
                <Input placeholder="Year" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Employee Count" name="employeeCount">
                <Input placeholder="Total Employees" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="India Headquarters" name="indiaHeadquarters">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Primary Industry" name="primaryIndustry">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Office Locations" name="officeLocations">
            <Input placeholder="Office Locations" />
          </Form.Item>

          <Form.Item label="Company Address" name="companyAddress" rules={[{ required: false, message: "Please enter company address" }]}>
            <Input />
          </Form.Item>

          {/* ================= SECTION 6: LEADERSHIP ================= */}
          <Card title="Leadership Team" style={{ marginBottom: 20 }} size="small" headStyle={{ backgroundColor: '#f0f2f5' }}>
            <Form.List name="leaderShip">
              {(fields, { add, remove }) => (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card
                        key={key}
                        size="small"
                        type="inner"
                        title={`Leader ${name + 1}`}
                        extra={<Button onClick={() => remove(name)} danger type="text" size="small">Remove</Button>}
                      >
                        <Form.Item {...restField} name={[name, 'leaderName']} label="Name" style={{ marginBottom: 8 }}>
                          <Input placeholder="Leader Name" />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'leaderDestination']} label="Designation" style={{ marginBottom: 8 }}>
                          <Input placeholder="Designation" />
                        </Form.Item>
                        <div style={{ marginTop: 8 }}>
                          <label style={{ fontSize: '12px', color: '#666' }}>Upload Image</label>
                          <input
                            type="file"
                            onChange={(e) => handleLeaderImageUpload(e, name)}
                            accept="image/png,image/jpg,image/jpeg,image/webp"
                            style={{ width: '100%', fontSize: '12px', marginTop: 4 }}
                          />
                          {leaderImageFiles[name] && (
                            <img
                              src={`https://service.ireedindia.com/image/${leaderImageFiles[name]}`}
                              alt="Leader"
                              style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "50%", marginTop: 8 }}
                            />
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                  <Button type="dashed" onClick={() => add()} block style={{ marginTop: 16 }}>+ Add Leader</Button>
                </>
              )}
            </Form.List>
          </Card>

          {/* ================= SECTION 7: CSR ================= */}
          <Card title="CSR Activities" style={{ marginBottom: 20 }} size="small" headStyle={{ backgroundColor: '#f0f2f5' }}>
            <Form.List name="csrActivities">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <Form.Item {...restField} name={[name, 'point']} noStyle>
                          <Input placeholder="Point" style={{ marginBottom: 4 }} />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'link']} noStyle>
                          <Input placeholder="Link (Optional)" size="small" />
                        </Form.Item>
                      </div>
                      <Button onClick={() => remove(name)} danger shape="circle" icon="x" size="small">x</Button>
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add()} block>+ npSR</Button>
                </>
              )}
            </Form.List>
          </Card>

          {/* ================= SECTION 8: BENEFITS & SOCIAL MEDIA (Paired) ================= */}
          <Row gutter={24}>
            {/* Benefits */}
            <Col xs={24} lg={12}>
              <Card title="Benefits" style={{ marginBottom: 20, height: '100%' }} size="small" headStyle={{ backgroundColor: '#f0f2f5' }}>
                <Form.List name="benefits">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          <Form.Item {...restField} name={[name, 'items']} style={{ flex: 1, marginBottom: 0 }}>
                            <Input placeholder="Benefit Item" />
                          </Form.Item>
                          <Button onClick={() => remove(name)} danger size="small">Remove</Button>
                        </div>
                      ))}
                      <Button type="dashed" onClick={() => add()} block>+ Add Benefit</Button>
                    </>
                  )}
                </Form.List>
              </Card>
            </Col>

            {/* Social Media */}
            <Col xs={24} lg={12}>
              <Card title="Social Media" style={{ marginBottom: 20, height: '100%' }} size="small" headStyle={{ backgroundColor: '#f0f2f5' }}>
                <Form.List name="companySocialMedia">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          <Form.Item {...restField} name={[name, 'platform']} style={{ width: '35%', marginBottom: 0 }}>
                            <Input placeholder="Platform" />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, 'url']} style={{ flex: 1, marginBottom: 0 }}>
                            <Input placeholder="URL" />
                          </Form.Item>
                          <Button onClick={() => remove(name)} danger size="small">x</Button>
                        </div>
                      ))}
                      <Button type="dashed" onClick={() => add()} block>+ Add Social</Button>
                    </>
                  )}
                </Form.List>
              </Card>
            </Col>
          </Row>

          {/* ================= SECTION 9: FAQ ================= */}
          <Card title="Interview FAQ" style={{ marginBottom: 20 }} size="small" headStyle={{ backgroundColor: '#f0f2f5' }}>
            <Form.List name="interviewFaq">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ marginBottom: 12, border: '1px solid #f0f0f0', padding: 8, borderRadius: 4 }}>
                      <Form.Item {...restField} name={[name, 'question']} noStyle>
                        <Input placeholder="Question" style={{ marginBottom: 4, fontWeight: 600 }} />
                      </Form.Item>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Form.Item {...restField} name={[name, 'answer']} noStyle style={{ flex: 1 }}>
                          <Input.TextArea placeholder="Answer" autoSize />
                        </Form.Item>
                        <Button onClick={() => remove(name)} danger size="small">x</Button>
                      </div>
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add()} block>+ Add FAQ</Button>
                </>
              )}
            </Form.List>
          </Card>

          {/* ================= SECTION 10: ADMIN CONTROLS (Moved to Bottom) ================= */}
          {isAdmin && (
            <Card style={{ background: '#fafafa', marginBottom: 20, borderColor: '#d9d9d9' }} size="small">
              <Row justify="space-between" align="middle">
                <Col>
                  <Form.Item label="Show in Footer" name="isFooterShow" valuePropName="checked" style={{ marginBottom: 0, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <Switch />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item label="Published" name="published" valuePropName="checked" initialValue={false} style={{ marginBottom: 0, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          <Form.Item wrapperCol={{ offset: 8, span: 16 }} style={{ textAlign: 'right' }}>
            <Button type="primary" htmlType="submit" size="large">
              {editEmployerIndex !== null ? "Update Employer" : "Create Employer"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  const renderCityManagement = () => (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <CityManagement />
    </div>
  );

  return (
    <div>
      <div style={{
        padding: '16px 24px',
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
      }}>
        <Segmented
          size="large"
          value={activeTab}
          onChange={(value: any) => setActiveTab(value)}
          options={[
            {
              label: (
                <div style={{ padding: '4px 16px' }}>
                  <Space>
                    <GlobalOutlined />
                    <span style={{ fontWeight: 500 }}>Employers</span>
                  </Space>
                </div>
              ),
              value: 'employer',
            },
            {
              label: (
                <div style={{ padding: '4px 16px' }}>
                  <Space>
                    <EnvironmentOutlined />
                    <span style={{ fontWeight: 500 }}>City Management</span>
                  </Space>
                </div>
              ),
              value: 'city',
            },
          ]}
          style={{
            background: '#f3f4f6',
            padding: '4px',
            borderRadius: '12px'
          }}
        />
      </div>

      {activeTab === 'employer' ? renderEmployerManagement() : renderCityManagement()}
    </div>
  );
};

// --- City Management Sub-component ---
const CityManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [cities, setCities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCity, setEditingCity] = useState<any>(null);

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    setLoading(true);
    try {
      const response = await axios.post("https://service.ireedindia.com/v1/find-city", {
        sortOrder: "A-Z"
      });
      // Handle response.data.data or response.data if it's directly an array
      const data = Array.isArray(response.data) ? response.data : (Array.isArray(response.data.data) ? response.data.data : []);
      setCities(data);
    } catch (error) {
      console.error("Error fetching cities:", error);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        state: values.state,
        capital: values.capital,
        city: values.city,
        isActive: values.isActive ?? true,
        isFooterShow: values.isFooterShow ?? true
      };

      if (editingCity) {
        await axios.patch(`https://service.ireedindia.com/v1/update-city/${editingCity._id}`, payload);
        message.success("City updated successfully");
      } else {
        await axios.post('https://service.ireedindia.com/v1/create-city', payload);
        message.success("City created successfully");
      }

      setIsModalVisible(false);
      setEditingCity(null);
      form.resetFields();
      fetchCities();
    } catch (error) {
      console.error("Error saving city:", error);
      message.error(`Failed to ${editingCity ? 'update' : 'create'} city`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      await axios.delete(`https://service.ireedindia.com/v1/delete-city/${id}`);
      message.success("City deleted successfully");
      fetchCities();
    } catch (error) {
      console.error("Error deleting city:", error);
      message.error("Failed to delete city");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: any) => {
    setEditingCity(record);
    form.setFieldsValue({
      city: record.city,
      state: record.state,
      capital: record.capital,
      isActive: record.isActive,
      isFooterShow: record.isFooterShow
    });
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: "City Name",
      dataIndex: "city",
      key: "city",
      render: (text: string) => <span style={{ fontWeight: 600, color: '#111827' }}>{text}</span>
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
    },
    {
      title: "Capital",
      dataIndex: "capital",
      key: "capital",
    },
    {
      title: "Footer Display",
      dataIndex: "isFooterShow",
      key: "isFooterShow",
      render: (isFooterShow: boolean) => (
        <Tag color={isFooterShow ? "blue" : "default"}>
          {isFooterShow ? "Visible in Footer" : "Hidden"}
        </Tag>
      )
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Badge
          status={isActive ? "success" : "error"}
          text={
            <Space size={4}>
              {isActive ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
              <span style={{ color: isActive ? '#065f46' : '#991b1b', fontWeight: 500 }}>
                {isActive ? "Active" : "Inactive"}
              </span>
            </Space>
          }
        />
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined style={{ color: '#4f46e5' }} />}
            type="text"
            onClick={() => handleEdit(record)}
          />
          <Button
            icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
            type="text"
            danger
            onClick={() => {
              if (window.confirm("Are you sure you want to delete this city?")) {
                handleDelete(record._id);
              }
            }}
          />
        </Space>
      )
    }
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f2937' }}>City Management</h2>
          <p style={{ color: '#6b7280', marginTop: 4 }}>Configure available cities for employer locations</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => {
            setEditingCity(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
          style={{ borderRadius: '8px', height: '48px', padding: '0 24px', fontWeight: 600 }}
        >
          Add New City
        </Button>
      </div>

      <Card style={{ borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
        <Table
          loading={loading}
          dataSource={cities}
          columns={columns}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={<div style={{ padding: '8px 0', fontSize: '20px', fontWeight: 700 }}>{editingCity ? 'Edit City' : 'Add New City'}</div>}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCity(null);
          form.resetFields();
        }}
        footer={null}
        centered
        width={500}
        bodyStyle={{ padding: '24px 32px' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ isActive: true, isFooterShow: true }}
        >
          <Form.Item name="city" label={<span style={{ fontWeight: 600 }}>City Name</span>} rules={[{ required: true, message: 'City is required' }]}>
            <Input placeholder="e.g. Amaravati" size="large" style={{ borderRadius: '6px' }} />
          </Form.Item>

          <Form.Item name="state" label={<span style={{ fontWeight: 600 }}>State</span>} rules={[{ required: true, message: 'State is required' }]}>
            <Input placeholder="e.g. Andhra Pradesh" size="large" style={{ borderRadius: '6px' }} />
          </Form.Item>

          <Form.Item name="capital" label={<span style={{ fontWeight: 600 }}>Capital</span>} rules={[{ required: true, message: 'Capital is required' }]}>
            <Input placeholder="e.g. Amaravati" size="large" style={{ borderRadius: '6px' }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isActive" label={<span style={{ fontWeight: 600 }}>Active Status</span>} valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isFooterShow" label={<span style={{ fontWeight: 600 }}>Show in Footer</span>} valuePropName="checked">
                <Switch checkedChildren="Show" unCheckedChildren="Hide" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 32 }}>
            <Button type="primary" htmlType="submit" size="large" block loading={loading} style={{ height: '48px', borderRadius: '8px', fontWeight: 700 }}>
              {editingCity ? 'Update City' : 'Create City'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default CreateEmployerComponent;